/**
 * Catalogue des variables de script et moteur de substitution partagé.
 *
 * Les scripts d'appel contiennent des marqueurs `{{clé}}` remplacés au moment
 * de l'affichage à l'agent par les données réelles du contact / de l'agent /
 * du moment. Ce module est la source unique de vérité :
 *  - `STANDARD_VARIABLE_GROUPS` alimente la palette d'insertion de l'éditeur
 *    (ScriptsView) ET la fiche d'aide ;
 *  - `substituteScriptVariables` est utilisé au rendu (ScriptModal) pour
 *    résoudre les marqueurs.
 *
 * Toute variable proposée dans la palette DOIT être résolue ici (et
 * inversement) — c'est la garantie qu'un marqueur inséré à l'écriture sera
 * effectivement remplacé pendant l'appel.
 */

export interface ScriptVariable {
  /** Clé telle qu'écrite entre accolades, ex. `name` → `{{name}}`. */
  key: string
  /** Libellé lisible affiché dans la palette. */
  label: string
  /** Exemple de valeur — sert à l'aperçu et à l'aide. */
  example: string
}

export interface ScriptVariableGroup {
  title: string
  variables: ScriptVariable[]
}

/** Variables disponibles partout (indépendantes d'une campagne précise). */
export const STANDARD_VARIABLE_GROUPS: ScriptVariableGroup[] = [
  {
    title: 'Contact',
    variables: [
      { key: 'name', label: 'Nom du contact', example: 'Marie Dupont' },
      { key: 'phone', label: 'Téléphone', example: '+33 6 12 34 56 78' },
      { key: 'email', label: 'Email', example: 'marie@exemple.fr' },
      { key: 'company', label: 'Société', example: 'Acme SARL' },
      { key: 'city', label: 'Ville', example: 'Lyon' },
    ],
  },
  {
    title: 'Agent',
    variables: [
      { key: 'agent_name', label: 'Votre nom complet', example: 'Julien Martin' },
      { key: 'agent_first_name', label: 'Votre prénom', example: 'Julien' },
      { key: 'team_name', label: 'Votre équipe', example: 'Ventes' },
    ],
  },
  {
    title: 'Date & heure',
    variables: [
      { key: 'date', label: 'Date du jour', example: '12/06/2026' },
      { key: 'time', label: 'Heure actuelle', example: '14:30' },
      { key: 'weekday', label: 'Jour de la semaine', example: 'vendredi' },
    ],
  },
]

/** Ensemble des clés standard — pour distinguer une clé « connue » d'un champ
 *  personnalisé inconnu lors de la mise en évidence. */
export const STANDARD_VARIABLE_KEYS = new Set(
  STANDARD_VARIABLE_GROUPS.flatMap((g) => g.variables.map((v) => v.key)),
)

export interface SubstituteContext {
  entry?: {
    name?: string | null
    phone?: string | null
    custom_data?: Record<string, any> | null
  } | null
  agent?: {
    fullName?: string
    firstName?: string | null
    teamName?: string | null
  } | null
  /** Instant de référence pour date/heure (défaut : maintenant). */
  now?: Date
}

/** Construit la table clé → valeur résolue pour un contexte donné. */
export function buildVariableMap(ctx: SubstituteContext): Record<string, string> {
  const now = ctx.now ?? new Date()
  const entry = ctx.entry
  const agent = ctx.agent
  const cd = (entry?.custom_data || {}) as Record<string, any>

  const map: Record<string, any> = {
    // Les champs personnalisés du contact en premier : ils peuvent fournir
    // email, company, city… puis on écrase avec les valeurs « fortes ».
    ...cd,
    name: entry?.name ?? '',
    phone: entry?.phone ?? '',
    agent_name: agent?.fullName ?? '',
    agent_first_name: agent?.firstName ?? '',
    team_name: agent?.teamName ?? '',
    date: now.toLocaleDateString('fr-FR'),
    time: now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
    weekday: now.toLocaleDateString('fr-FR', { weekday: 'long' }),
  }

  // Normalise en chaînes.
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(map)) {
    out[k] = v === undefined || v === null ? '' : String(v)
  }
  return out
}

/** Remplace les marqueurs `{{clé}}` (et `{{custom.clé}}`) via une table
 *  clé→valeur. `onMissing` décide du rendu d'une clé absente. */
function replaceTokens(
  text: string,
  vars: Record<string, string>,
  onMissing: (key: string) => string,
): string {
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, raw) => {
    const key = String(raw).replace(/^custom\./, '')
    const v = vars[key]
    return v === undefined || v === null ? onMissing(key) : String(v)
  })
}

/**
 * Remplace les marqueurs `{{clé}}` (et la forme historique `{{custom.clé}}`)
 * par les valeurs résolues. Un marqueur inconnu est remplacé par une chaîne
 * vide — comportement historique.
 */
export function substituteScriptVariables(text: string, ctx: SubstituteContext): string {
  if (!text) return text
  const vars = buildVariableMap(ctx)
  return replaceTokens(text, vars, () => '')
}

/**
 * Rendu d'APERÇU pour l'éditeur : remplace chaque variable connue par sa
 * valeur d'exemple. Une variable inconnue est laissée telle quelle entre
 * accolades pour signaler au rédacteur qu'elle ne sera pas résolue.
 *
 * `extraGroups` permet d'injecter les champs de campagne (clé/example) en
 * plus du catalogue standard.
 */
export function previewScriptVariables(
  text: string,
  extraGroups: ScriptVariableGroup[] = [],
): string {
  if (!text) return text
  const map: Record<string, string> = {}
  for (const g of [...STANDARD_VARIABLE_GROUPS, ...extraGroups]) {
    for (const v of g.variables) map[v.key] = v.example
  }
  return replaceTokens(text, map, (key) => `{{${key}}}`)
}
